import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { adoptUserLanguage } from "@/lib/i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserRaw] = useState(null);
  // L'account Ufficio usa le schermate dell'admin; paghe e impostazioni restano del titolare
  const setUser = (u) => setUserRaw(u && u.role === "ufficio" ? { ...u, role: "admin", office: true } : u);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("iot_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      adoptUserLanguage(data);
    } catch (_e) {
      localStorage.removeItem("iot_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Al ritorno da Google lascia lavorare AuthCallback
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    loadMe();
  }, [loadMe]);

  const login = async (identifier, password) => {
    const value = (identifier || "").trim();
    // Dipendenti usano il telefono; admin usa email.
    const isPhone = /^(\+?\d|00\d)/.test(value) || !value.includes("@");
    const payload = isPhone
      ? { phone: value, password }
      : { email: value, password };
    const { data } = await api.post("/auth/login", payload);
    localStorage.setItem("iot_token", data.access_token);
    adoptUserLanguage(data.user);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google", { session_id: sessionId });
    localStorage.setItem("iot_token", data.access_token);
    adoptUserLanguage(data.user);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("iot_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, logout, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
